/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type PageType from '@linc-technologies/linced-common/enums/page-type';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: posts.PostTemplate.
export default class PostTemplate extends Model {
  declare [Type]: 'posts/post-template';

  // schoolId specifies the school that the Post Template is for.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId specifies the school campus that this Post Template is for.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // createTime is the Post Template's creation date/time (UNIX timestamp).
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the Post Template's update date/time (UNIX timestamp).
  @attr('number')
  declare updateTime: number | undefined;

  // templateId is the ID of the template used to create this Post Template.
  @attr('string', { defaultValue: '' })
  declare templateId: string;

  // authorId is the person who created the Post Template.
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  // type specifies the post content type. For example - learner, staff,
  // school private, school public
  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PageType> | 0;

  // sequence is used to determine the order in which Post Templates are
  // displayed.
  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  // isLocked determines whether this Post Template is locked for editing by
  // the school.
  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  // Post Template Configuration
  // name is the name of this Post Template.
  @attr('string', { defaultValue: '' })
  declare name: string;

  // description explains the purpose of this Post Template.
  @attr('string', { defaultValue: '' })
  declare description: string;

  // postTemplate is the actual template content.
  @attr('string', { defaultValue: '' })
  declare postTemplate: string;

  // editableRoot determines whether the root element should be editable when
  // this Post Template is used to create a Post.
  @attr('boolean', { defaultValue: false })
  declare editableRoot: boolean;

  // Post Template Visibility
  // visibleToParents allows caregiver accounts to see/use the Post Template.
  @attr('boolean', { defaultValue: false })
  declare visibleToParents: boolean;

  // visibleToLearners allows Learner accounts to see/use the Post Template.
  @attr('boolean', { defaultValue: false })
  declare visibleToLearners: boolean;
}
